/**
 * AllArticlesPage — public listing of all published blog articles.
 *
 * Used at /articles. Linked from BlogArticlesEmbed "View all articles" and ArticleDetailPage back link.
 * Shows featured articles on top, then the rest with load more. No duplicate cards.
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Heart, Eye, ChevronRight, Star } from 'lucide-react';
import {
  getPublishedArticles,
  getFeaturedPublishedArticles,
  estimateReadTime,
  formatDate,
  type BlogArticle,
  type QueryDocumentSnapshot,
} from '@/lib/blog';
import type { DocumentData } from 'firebase/firestore';
import { setPageMeta } from '@/utils/metaTags';

const PAGE_SIZE = 9;
const FEATURED_LIMIT = 6;

export function AllArticlesPage(): JSX.Element {
  const [featuredArticles, setFeaturedArticles] = useState<BlogArticle[]>([]);
  const [articles, setArticles] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [featuredRes, allRes] = await Promise.all([
        getFeaturedPublishedArticles(FEATURED_LIMIT),
        getPublishedArticles(PAGE_SIZE),
      ]);
      setFeaturedArticles(featuredRes.articles);
      setArticles(allRes.articles);
      setLastDoc(allRes.lastDoc);
      setHasMore(allRes.articles.length === PAGE_SIZE);
    } catch (err) {
      console.error('[AllArticlesPage] Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPageMeta({
      title: 'All Articles | Insights',
      description: 'Browse all insights and articles on federal subcontracting, prime contractor outreach, and business development.',
    });
    loadInitial();
  }, [loadInitial]);

  async function loadMore() {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const { articles: more, lastDoc: last } = await getPublishedArticles(PAGE_SIZE, lastDoc);
      setArticles((prev) => [...prev, ...more]);
      setLastDoc(last);
      setHasMore(more.length === PAGE_SIZE);
    } catch (err) {
      console.error('[AllArticlesPage] Failed to load more articles:', err);
    } finally {
      setLoadingMore(false);
    }
  }

  const featuredIds = useMemo(
    () => new Set(featuredArticles.map((a) => a.id)),
    [featuredArticles]
  );
  const restArticles = useMemo(
    () => articles.filter((a) => !featuredIds.has(a.id)),
    [articles, featuredIds]
  );

  const contentClass = 'max-w-5xl mx-auto w-full px-4 sm:px-8 py-10 sm:py-12';

  if (loading) {
    return (
      <div className={contentClass}>
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      </div>
    );
  }

  const hasAnyArticles = featuredArticles.length > 0 || articles.length > 0;
  if (!hasAnyArticles) {
    return (
      <div className={contentClass}>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">All Articles</h1>
        <div className="py-12 text-center">
          <p className="text-gray-500">No articles published yet. Check back soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={contentClass}>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">All Articles</h1>
      <p className="text-gray-500 text-sm mb-10">
        Insights on federal subcontracting, prime outreach, and business development.
      </p>

      {/* Featured section on top */}
      {featuredArticles.length > 0 && (
        <section className="mb-12" aria-label="Featured articles">
          <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Star size={18} className="text-amber-500 fill-amber-500" />
            Featured
          </h2>
          <p className="text-sm text-gray-500 mb-6">Editor’s picks</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>
      )}

      {/* Rest of articles (exclude featured to avoid duplicates) */}
      <section aria-label="All articles">
        {featuredArticles.length > 0 && (
          <>
            <h2 className="text-lg font-semibold text-gray-900 mb-6">All articles</h2>
          </>
        )}
        {restArticles.length === 0 ? (
          featuredArticles.length > 0 && (
            <p className="text-sm text-gray-500">No additional articles yet.</p>
          )
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {restArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            <div className="mt-10 flex flex-col items-center gap-4">
              {hasMore && (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
                >
                  {loadingMore ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent" />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                  {loadingMore ? 'Loading…' : 'Load more articles'}
                </button>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function ArticleCard({ article }: { article: BlogArticle }): JSX.Element {
  const readTime = estimateReadTime(article.body);
  const tags = article.tags?.slice(0, 2) ?? [];

  return (
    <Link
      to={`/insights/${article.slug}`}
      className="group flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
    >
      {article.featuredImage ? (
        <div className="aspect-[16/9] overflow-hidden bg-gray-100">
          <img
            src={article.featuredImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : (
        <div className="aspect-[16/9] bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <span className="text-4xl font-bold text-blue-200">{article.title.charAt(0)}</span>
        </div>
      )}

      <div className="flex flex-col flex-1 p-4">
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <h3 className="font-semibold text-gray-900 leading-snug group-hover:text-blue-600 transition-colors line-clamp-2">
          {article.title}
        </h3>

        {article.excerpt && (
          <p className="mt-1.5 text-sm text-gray-500 line-clamp-2 flex-1">{article.excerpt}</p>
        )}

        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-400">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <Calendar size={11} />
              {formatDate(article.publishedAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock size={11} />
              {readTime}m read
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <Heart size={11} />
              {article.likeCount ?? 0}
            </span>
            {article.showViewCount && (
              <span className="inline-flex items-center gap-1">
                <Eye size={11} />
                {article.viewCount ?? 0}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
